import React from "react";
import {
  REGISTRATION_MARKETING_CONSENT_TEXT,
  REGISTRATION_MARKETING_HELPER_TEXT,
} from "constraints/consent";

type Props = {
  checked: boolean;
  onChange: (checked: boolean) => void;
  /** Distinguishes instances when more than one form is on a page. */
  id?: string;
};

/**
 * Marketing consent checkbox for the registration forms.
 *
 * Deliberate properties, all of which are requirements rather than styling
 * choices:
 *
 *  - **Unchecked on first render, everywhere.** No `defaultChecked`, no
 *    geo-conditional initial state, no locale-conditional initial state. The
 *    caller owns the value and initialises it to `false`.
 *  - **Never blocking.** It is not part of form validation and has no `required`
 *    attribute. Registration succeeds ticked or unticked.
 *  - **Wording is a constant, not an i18n key.** The backend records the exact
 *    string shown, so it cannot be resolved per-locale or changed retroactively
 *    under a record that already exists.
 *
 * The single checkbox covers all three marketing purposes, which is why its
 * wording enumerates them. They become independently controllable in the
 * preference centre afterwards.
 */
export const MarketingConsentCheckbox = ({ checked, onChange, id = "marketingConsent" }: Props) => (
  <div className="flex flex-col gap-1">
    <label htmlFor={id} className="flex items-start gap-2.5 mb-0 cursor-pointer">
      <input
        id={id}
        name={id}
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 h-4 w-4 shrink-0 accent-primary cursor-pointer"
      />
      <span className="text-[13px] leading-snug text-ink-2">
        {REGISTRATION_MARKETING_CONSENT_TEXT}
      </span>
    </label>
    <p className="text-xs leading-snug text-ink-4 pl-[26px]">
      {REGISTRATION_MARKETING_HELPER_TEXT}
    </p>
  </div>
);
